import { useState, useEffect, useCallback } from 'react'
import { gazoKensakuService } from '../../web/services/gazoKensakuService.js'

export const useGazoKensaku = () => {
  // State for categories and tags
  const [satsueiMemoCategories, setSatsueiMemoCategories] = useState([])
  const [imageTagCategories, setImageTagCategories] = useState([])
  const [imageTags, setImageTags] = useState([])
  
  // State for search form
  const [objectIds, setObjectIds] = useState('')
  const [selectedImageTags, setSelectedImageTags] = useState([])
  const [imageTagSearchType, setImageTagSearchType] = useState('1') // 0: any tag, 1: all tags
  const [imageTagSelector, setImageTagSelector] = useState('1') // 1: select, 2: input
  const [selectedSatsueiMemoCategory, setSelectedSatsueiMemoCategory] = useState('')
  const [selectedImageTagCategory, setSelectedImageTagCategory] = useState('')
  const [selectedImageTag, setSelectedImageTag] = useState('')
  const [inputImageTag, setInputImageTag] = useState('')
  
  // State for search results
  const [objectImages, setObjectImages] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  
  // State for pagination
  const [currentPage, setCurrentPage] = useState(0)
  const [itemsPerPage, setItemsPerPage] = useState(30)
  const [currentPageRange] = useState(3)

  // Load satsuei memo categories
  const loadSatsueiMemoCategories = useCallback(async () => {
    try {
      setError(null) // Clear any previous errors
      const response = await gazoKensakuService.getSatsueiMemoCategory()
      setSatsueiMemoCategories(response.satsueiMemoCategoryList || [])
    } catch (error) {
      console.error('Failed to load satsuei memo categories:', error)
      setError('撮影メモカテゴリの読み込みに失敗しました')
    }
  }, [])

  // Load satsuei memo categories on mount
  useEffect(() => {
    loadSatsueiMemoCategories()
  }, [loadSatsueiMemoCategories])

  // Load image tag categories when satsuei memo category changes
  const loadImageTagCategories = useCallback(async (satsueiMemoCategoryId) => {
    if (!satsueiMemoCategoryId) {
      setImageTagCategories([])
      setImageTags([])
      return
    }

    try {
      setError(null) // Clear any previous errors
      const response = await gazoKensakuService.getImageTagCategoryList(satsueiMemoCategoryId)
      setImageTagCategories(response.imageTagCategoryList || [])
    } catch (error) {
      console.error('Failed to load image tag categories:', error)
      setError('画像タグカテゴリの読み込みに失敗しました')
    }
  }, [])

  // Load image tags when image tag category changes
  const loadImageTags = useCallback(async (satsueiMemoCategoryId, imageTagCategoryId) => {
    if (!satsueiMemoCategoryId || !imageTagCategoryId) {
      setImageTags([])
      return
    }

    try {
      setError(null) // Clear any previous errors
      const response = await gazoKensakuService.getImageTagList(satsueiMemoCategoryId, imageTagCategoryId)
      setImageTags(response.imageTagList || [])
    } catch (error) {
      console.error('Failed to load image tags:', error)
      setError('画像タグの読み込みに失敗しました')
    }
  }, [])

  // Handle satsuei memo category change
  const handleSatsueiMemoCategoryChange = useCallback((categoryId) => {
    setSelectedSatsueiMemoCategory(categoryId)
    setSelectedImageTagCategory('')
    setSelectedImageTag('')
    setImageTagCategories([])
    setImageTags([])
    
    if (categoryId) {
      loadImageTagCategories(categoryId)
    }
  }, [loadImageTagCategories])

  // Handle image tag category change
  const handleImageTagCategoryChange = useCallback((categoryId) => {
    setSelectedImageTagCategory(categoryId)
    setSelectedImageTag('')
    setImageTags([])
    
    if (categoryId && selectedSatsueiMemoCategory) {
      loadImageTags(selectedSatsueiMemoCategory, categoryId)
    }
  }, [selectedSatsueiMemoCategory, loadImageTags])

  // Add image tag to search
  const addImageTag = useCallback((imageTag) => {
    if (!imageTag || imageTag.trim() === '') {
      return
    }

    const trimmedTag = imageTag.trim()
    
    // Check if already exists
    const existingTag = selectedImageTags.find(tag => tag.imageTag === trimmedTag)
    if (existingTag) {
      if (!existingTag.deleteFlg) {
        // Show error message
        setError('すでに指定されています')
        return
      } else {
        // Re-enable existing tag
        setSelectedImageTags(prev => 
          prev.map(tag => 
            tag.imageTag === trimmedTag 
              ? { ...tag, deleteFlg: 0 }
              : tag
          )
        )
        return
      }
    }

    // Add new tag
    const newTag = {
      imageTag: trimmedTag,
      deleteFlg: 0
    }
    setSelectedImageTags(prev => [...prev, newTag])
    
    // Clear input
    setInputImageTag('')
    setSelectedImageTag('')
  }, [selectedImageTags])

  // Remove image tag from search
  const removeImageTag = useCallback((imageTag) => {
    setSelectedImageTags(prev => 
      prev.map(tag => 
        tag.imageTag === imageTag 
          ? { ...tag, deleteFlg: 1 }
          : tag
      )
    )
  }, [])

  // Clear all search conditions
  const clearSearchConditions = useCallback(() => {
    setObjectIds('')
    setSelectedImageTags([])
    setImageTagSearchType('1')
    setImageTagSelector('1')
    setSelectedSatsueiMemoCategory('')
    setSelectedImageTagCategory('')
    setSelectedImageTag('')
    setInputImageTag('')
    setImageTagCategories([])
    setImageTags([])
    setCurrentPage(0)
  }, [])

  // Perform search
  const performSearch = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    setCurrentPage(0)

    try {
      // Prepare search parameters
      const searchParams = {}
      
      // Object IDs
      if (objectIds.trim()) {
        searchParams.objectIds = objectIds.split('\n').map(id => id.trim()).filter(id => id)
      }
      
      // Image tags (only active ones)
      const activeImageTags = selectedImageTags
        .filter(tag => !tag.deleteFlg)
        .map(tag => tag.imageTag)
      
      if (activeImageTags.length > 0) {
        searchParams.imageTags = activeImageTags
        searchParams.imageTagSearchType = imageTagSearchType
      }

      const response = await gazoKensakuService.getObjectImageList(searchParams)
      
      // Flatten object images from all object datas
      const allObjectImages = []
      if (response.objectDatas) {
        for (const objectData of response.objectDatas) {
          if (objectData.objectImages) {
            allObjectImages.push(...objectData.objectImages)
          }
        }
      }
      
      setObjectImages(allObjectImages)
    } catch (error) {
      console.error('Search failed:', error)
      setError('検索に失敗しました')
      setObjectImages([])
    } finally {
      setIsLoading(false)
    }
  }, [objectIds, selectedImageTags, imageTagSearchType])

  // Calculate pagination
  const totalCount = objectImages.length
  const totalPages = Math.ceil(totalCount / itemsPerPage)
  
  // Calculate page range for display
  let pageStart = Math.max(0, currentPage - Math.floor(currentPageRange / 2))
  let pageEnd = totalPages > 0 ? Math.min(totalPages - 1, pageStart + currentPageRange - 1) : 0
  
  // Adjust if we're near the end
  if (totalPages > 0 && pageEnd === totalPages - 1) {
    pageStart = Math.max(0, pageEnd - currentPageRange + 1)
  }

  // Get current page items
  const startIndex = currentPage * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const currentPageItems = objectImages.slice(startIndex, endIndex)

  // Navigation functions
  const goToPage = useCallback((page) => {
    if (page >= 0 && page < totalPages) {
      setCurrentPage(page)
    }
  }, [totalPages])

  const goToFirstPage = useCallback(() => {
    setCurrentPage(0)
  }, [])

  const goToLastPage = useCallback(() => {
    if (totalPages > 0) {
      setCurrentPage(totalPages - 1)
    }
  }, [totalPages])

  const goToPreviousPage = useCallback(() => {
    setCurrentPage(prev => Math.max(0, prev - 1))
  }, [])

  const goToNextPage = useCallback(() => {
    if (totalPages === 0) return; // Don't navigate if no pages
    setCurrentPage(prev => Math.min(totalPages - 1, prev + 1))
  }, [totalPages])

  const changeItemsPerPage = useCallback((newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage)
    setCurrentPage(0)
  }, [])

  return {
    // Categories and tags
    satsueiMemoCategories,
    imageTagCategories,
    imageTags,
    
    // Search form state
    objectIds,
    setObjectIds,
    selectedImageTags,
    imageTagSearchType,
    setImageTagSearchType,
    imageTagSelector,
    setImageTagSelector,
    selectedSatsueiMemoCategory,
    selectedImageTagCategory,
    selectedImageTag,
    setSelectedImageTag,
    inputImageTag,
    setInputImageTag,
    
    // Search results
    objectImages,
    isLoading,
    error,
    setError,
    
    // Pagination
    currentPage,
    itemsPerPage,
    totalCount,
    totalPages,
    pageStart,
    pageEnd,
    currentPageItems,
    
    // Functions
    handleSatsueiMemoCategoryChange,
    handleImageTagCategoryChange,
    addImageTag,
    removeImageTag,
    clearSearchConditions,
    performSearch,
    goToPage,
    goToFirstPage,
    goToLastPage,
    goToPreviousPage,
    goToNextPage,
    changeItemsPerPage
  }
}
